import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faShieldAlt,
    faSpinner,
    faDownload,
    faTriangleExclamation,
    faCircleCheck,
    faCircleInfo,
    faBookOpen,
    faArrowRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import { DEFAULT_SCREENING_SENSITIVITY } from '../../utils/screeningDefaults';
import { getErrorMessage } from '../../utils/errorMessages';
import './AdHocRiskCheckModal.css';

/**
 * Ad-hoc sanctions + AI risk check on a counterparty that is NOT in the CRM.
 *
 * Deliberately creates no partner record: this is the "someone just emailed us
 * out of nowhere" case. The generated report is filed into Knowledge Hub →
 * General → Partner Info under the typed name, and can be linked to a real
 * partner later if the prospect is onboarded.
 *
 * Note this is NOT the same analysis as a saved partner's "AI Risk Analysis"
 * tab — that one scores internal HMS history, which does not exist here. The
 * copy keeps the two visibly distinct so nobody compares the scores.
 */

const RISK_LEVELS = {
    low: { label: 'Low Risk', className: 'adhoc-risk-low', icon: faCircleCheck },
    medium: { label: 'Medium Risk', className: 'adhoc-risk-medium', icon: faTriangleExclamation },
    high: { label: 'High Risk', className: 'adhoc-risk-high', icon: faTriangleExclamation },
    critical: { label: 'Critical Risk', className: 'adhoc-risk-critical', icon: faTriangleExclamation },
    unknown: { label: 'Not Assessed', className: 'adhoc-risk-unknown', icon: faCircleInfo }
};

const AdHocRiskCheckModal = ({ isOpen, onClose }) => {
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [country, setCountry] = useState('');
    const [sensitivity, setSensitivity] = useState(DEFAULT_SCREENING_SENSITIVITY);
    const [guidance, setGuidance] = useState('');
    const [includeWeb, setIncludeWeb] = useState(true);

    const [isRunning, setIsRunning] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [quota, setQuota] = useState(null);

    const loadQuota = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/crm/adhoc-risk-check/quota/');
            setQuota(response.data?.quota || null);
        } catch (err) {
            // Quota display is informational — a failure here must not block the
            // form. The server enforces the cap regardless of what we render.
            setQuota(null);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadQuota();
        }
    }, [isOpen, loadQuota]);

    if (!isOpen) return null;

    const resetForm = () => {
        setResult(null);
        setError('');
        setName('');
        setAddress('');
        setCountry('');
        setGuidance('');
        setSensitivity(DEFAULT_SCREENING_SENSITIVITY);
        setIncludeWeb(true);
    };

    const handleClose = () => {
        if (isRunning) return;
        resetForm();
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) handleClose();
    };

    const runCheck = async () => {
        if (!name.trim()) {
            setError('Name is required.');
            return;
        }
        if (!address.trim()) {
            setError('Address is required — the sanctions provider needs it to match reliably.');
            return;
        }

        setIsRunning(true);
        setError('');
        setResult(null);

        try {
            const response = await axiosInstance.post('/api/crm/adhoc-risk-check/', {
                name: name.trim(),
                address: address.trim(),
                country: country.trim(),
                sensitivity,
                user_guidance: guidance.trim(),
                include_web_research: includeWeb
            });

            setResult(response.data);
            if (response.data?.quota) setQuota(response.data.quota);
        } catch (err) {
            setError(getErrorMessage(err, 'The risk check could not be completed. No report was generated.'));
            if (err?.response?.data?.quota) setQuota(err.response.data.quota);
        } finally {
            setIsRunning(false);
        }
    };

    const downloadPdf = () => {
        if (!result?.pdf_base64) return;
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${result.pdf_base64}`;
        link.download = `${(result.name || 'counterparty').replace(/[^a-z0-9]/gi, '_')}_adhoc_risk_report.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const risk = RISK_LEVELS[result?.risk_level] || RISK_LEVELS.unknown;
    const analysis = result?.risk_analysis || {};
    const outOfQuota = quota && quota.remaining <= 0;

    const renderList = (items) => (
        <ul className="adhoc-list">
            {items.map((item, i) => <li key={i}>{String(item)}</li>)}
        </ul>
    );

    return (
        <div className="adhoc-modal-overlay" onClick={handleOverlayClick}>
            <div className="adhoc-modal">
                <div className="adhoc-modal-header">
                    <div className="adhoc-modal-header-content">
                        <h3><FontAwesomeIcon icon={faShieldAlt} /> Ad-hoc Risk Check</h3>
                        <div className="adhoc-modal-subtitle">
                            Screen a counterparty that is not yet a partner — no profile is created
                        </div>
                    </div>
                    <button className="adhoc-close-button" onClick={handleClose} type="button" disabled={isRunning}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="adhoc-modal-body">
                    {!result && (
                        <>
                            <div className="adhoc-notice">
                                <FontAwesomeIcon icon={faCircleInfo} />
                                <span>
                                    Runs a live sanctions/PEP screening
                                    {includeWeb ? ' plus open-web background research' : ''}, then files a
                                    PDF report in Knowledge Hub → General → Partner Info. This is a
                                    preliminary check, not due diligence.
                                </span>
                            </div>

                            {quota && (
                                <div className={`adhoc-quota ${outOfQuota ? 'adhoc-quota-empty' : ''}`}>
                                    <strong>{quota.remaining}</strong> of {quota.limit} checks left today
                                    <span className="adhoc-quota-hint">
                                        Each check queries a paid screening service.
                                    </span>
                                </div>
                            )}

                            <div className="adhoc-field">
                                <label htmlFor="adhoc-name">Counterparty name <span className="adhoc-required">*</span></label>
                                <input
                                    id="adhoc-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Unknown Trading FZE"
                                    disabled={isRunning}
                                />
                            </div>

                            <div className="adhoc-field">
                                <label htmlFor="adhoc-address">Address <span className="adhoc-required">*</span></label>
                                <input
                                    id="adhoc-address"
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    placeholder="Full address as given to you"
                                    disabled={isRunning}
                                />
                            </div>

                            <div className="adhoc-field-row">
                                <div className="adhoc-field">
                                    <label htmlFor="adhoc-country">Country / jurisdiction</label>
                                    <input
                                        id="adhoc-country"
                                        type="text"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        placeholder="Optional"
                                        disabled={isRunning}
                                    />
                                </div>
                                <div className="adhoc-field">
                                    <label htmlFor="adhoc-sensitivity">
                                        Match sensitivity: <strong>{sensitivity}%</strong>
                                    </label>
                                    <input
                                        id="adhoc-sensitivity"
                                        type="range"
                                        min="50"
                                        max="100"
                                        value={sensitivity}
                                        onChange={(e) => setSensitivity(Number(e.target.value))}
                                        disabled={isRunning}
                                    />
                                    <div className="adhoc-hint">Higher = stricter matching, fewer hits</div>
                                </div>
                            </div>

                            <div className="adhoc-field">
                                <label htmlFor="adhoc-guidance">What should the research focus on?</label>
                                <textarea
                                    id="adhoc-guidance"
                                    rows="2"
                                    value={guidance}
                                    onChange={(e) => setGuidance(e.target.value)}
                                    placeholder="Optional — e.g. ownership structure, recent litigation, export controls"
                                    disabled={isRunning}
                                />
                            </div>

                            <label className="adhoc-checkbox">
                                <input
                                    type="checkbox"
                                    checked={includeWeb}
                                    onChange={(e) => setIncludeWeb(e.target.checked)}
                                    disabled={isRunning}
                                />
                                <span>
                                    Include open-web &amp; adverse-media research
                                    <span className="adhoc-hint"> — richer report, adds roughly 20–40 seconds</span>
                                </span>
                            </label>

                        </>
                    )}

                    {result && (
                        <div className="adhoc-result">
                            <div className={`adhoc-risk-banner ${risk.className}`}>
                                <FontAwesomeIcon icon={risk.icon} />
                                <div>
                                    <div className="adhoc-risk-level">{risk.label}</div>
                                    <div className="adhoc-risk-subject">{result.name}</div>
                                </div>
                                {analysis.confidence && (
                                    <div className="adhoc-confidence">
                                        {String(analysis.confidence).toUpperCase()} CONFIDENCE
                                    </div>
                                )}
                            </div>

                            <div className={`adhoc-screening ${result.suspect ? 'adhoc-screening-hit' : 'adhoc-screening-clear'}`}>
                                <strong>Sanctions screening:</strong>{' '}
                                {result.suspect
                                    ? `MATCH FOUND — ${result.screening?.hitListName || 'listed entity'} (score ${result.highest_score}%)`
                                    : `no match above the ${result.sensitivity}% threshold (score ${result.highest_score}%)`}
                            </div>

                            {analysis.executive_summary && (
                                <div className="adhoc-section">
                                    <h4>Executive summary</h4>
                                    <p>{analysis.executive_summary}</p>
                                </div>
                            )}

                            {Array.isArray(analysis.red_flags) && analysis.red_flags.length > 0 && (
                                <div className="adhoc-section adhoc-section-danger">
                                    <h4>Red flags</h4>
                                    {renderList(analysis.red_flags)}
                                </div>
                            )}

                            {Array.isArray(analysis.key_findings) && analysis.key_findings.length > 0 && (
                                <div className="adhoc-section">
                                    <h4>Key findings</h4>
                                    {renderList(analysis.key_findings)}
                                </div>
                            )}

                            {Array.isArray(analysis.information_gaps) && analysis.information_gaps.length > 0 && (
                                <div className="adhoc-section adhoc-section-muted">
                                    <h4>What we could not see</h4>
                                    {renderList(analysis.information_gaps)}
                                </div>
                            )}

                            {Array.isArray(analysis.recommended_next_steps) && analysis.recommended_next_steps.length > 0 && (
                                <div className="adhoc-section">
                                    <h4>Recommended next steps</h4>
                                    {renderList(analysis.recommended_next_steps)}
                                </div>
                            )}

                            {!result.web_research_succeeded && (
                                <div className="adhoc-warning">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <span>
                                        Open-web research was unavailable for this check — the assessment
                                        rests on the sanctions screening alone.
                                    </span>
                                </div>
                            )}

                            <div className={`adhoc-filed ${result.filed_to_knowledge_hub ? '' : 'adhoc-filed-failed'}`}>
                                <FontAwesomeIcon icon={faBookOpen} />
                                {result.filed_to_knowledge_hub
                                    ? 'Report filed in Knowledge Hub → General → Partner Info'
                                    : 'Report could NOT be filed in the Knowledge Hub — download it before closing.'}
                            </div>
                        </div>
                    )}
                </div>

                {/* Errors live OUTSIDE the scrolling body, pinned above the
                    footer. Inside it they rendered below the fold on a normal
                    laptop viewport, so pressing "Run check" with a missing field
                    looked like nothing happened at all. */}
                {error && (
                    <div className="adhoc-error adhoc-error-pinned">
                        <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
                    </div>
                )}

                <div className="adhoc-modal-footer">
                    {result ? (
                        <>
                            <button className="adhoc-btn-secondary" onClick={resetForm} type="button">
                                <FontAwesomeIcon icon={faArrowRotateLeft} /> Check another
                            </button>
                            <div className="adhoc-footer-right">
                                <button className="adhoc-btn-secondary" onClick={handleClose} type="button">
                                    Close
                                </button>
                                <button className="adhoc-btn-primary" onClick={downloadPdf} type="button">
                                    <FontAwesomeIcon icon={faDownload} /> Download report
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <button className="adhoc-btn-secondary" onClick={handleClose} type="button" disabled={isRunning}>
                                Cancel
                            </button>
                            <button
                                className="adhoc-btn-primary"
                                onClick={runCheck}
                                type="button"
                                disabled={isRunning || outOfQuota}
                                title={outOfQuota ? 'Daily limit reached' : undefined}
                            >
                                {isRunning ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Screening…</>
                                ) : (
                                    <><FontAwesomeIcon icon={faShieldAlt} /> Run check</>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdHocRiskCheckModal;
